// Collapsible-flashbar selection logic, derived from AWS Cloudscape
// `@cloudscape-design/components/flashbar` (Apache-2.0). NotificationFlashbar
// uses it to pick which notices render as visible stacked cards when collapsed.
//
// Notices carry a `msg_type` string (error / info / success / warning),
// which we treat as the item's "color". Unlike Cloudscape we have no
// in-progress type, so there are 4 possible colors.

import { type NoticeType } from './notice-icons';

const POSSIBLE_TYPES: ReadonlyArray<NoticeType> = ['error', 'info', 'success', 'warning'];

export interface FlashbarItem {
  msg_type?: string;
}

export function getItemColor(item: FlashbarItem): string {
  return item.msg_type || 'info';
}

// Returns a selection of notices, preserving order when possible but making
// sure every distinct color present in the stack is represented in the
// result. Order matches collapsed-stack render order (newest first).
// `desiredLength` is the number of stacked cards to show.
export function getVisibleCollapsedItems<T extends FlashbarItem>(
  items: T[],
  desiredLength: number,
): T[] {
  // First `desiredLength` items, each flagged with whether its color already
  // appeared earlier (so it can be replaced later if a fresh color needs room).
  const itemsOnTop: Array<{ item: T; isColorRepeated: boolean }> = [];
  // Items beyond `desiredLength` introducing a new color → promote in.
  const itemsToPromote: T[] = [];

  const addedColors = new Set<string>();
  const allPossibleColors = POSSIBLE_TYPES.length;
  const finalLength = Math.min(items.length, desiredLength);

  for (let i = 0; i < items.length; i++) {
    const item = items[i]!;
    const color = getItemColor(item);
    const isColorRepeated = addedColors.has(color);
    if (i < finalLength) {
      itemsOnTop.push({ item, isColorRepeated });
    } else {
      if (addedColors.size === allPossibleColors) break; // every color covered
      if (!isColorRepeated) itemsToPromote.push(item);
    }
    addedColors.add(color);
  }

  // Walk itemsOnTop from the end so newest items survive; older
  // color-duplicate ones get evicted to free slots for promotable items.
  const reversedInitialSelection: T[] = [];
  let slotsReservedForPromotions = 0;
  for (let j = itemsOnTop.length - 1; j >= 0; j--) {
    const entry = itemsOnTop[j]!;
    if (entry.isColorRepeated && slotsReservedForPromotions < itemsToPromote.length) {
      slotsReservedForPromotions += 1;
    } else {
      reversedInitialSelection.push(entry.item);
    }
  }
  const selectedItems = reversedInitialSelection.reverse();
  for (let k = 0; selectedItems.length < desiredLength && k < itemsToPromote.length; k++) {
    selectedItems.push(itemsToPromote[k]!);
  }
  return selectedItems;
}

// Number of visible cards when collapsed: at least 3, up to the count of
// distinct colors, capped by total items. Mirrors Cloudscape: 4 distinct-typed
// notices show 4 cards, 4 same-typed notices show 3.
export function getStackDepth(items: ReadonlyArray<FlashbarItem>): number {
  const distinctColors = new Set(items.map(getItemColor)).size;
  return Math.min(Math.max(distinctColors, 3), items.length);
}
